const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").trim();
const API_BASE = (configuredBaseUrl || "/api").replace(/\/+$/, "");

function toFriendlyFocalxDownloadMessage(rawMessage, status) {
  const message = String(rawMessage || "").trim();

  if (/Focalx file is missing on server/i.test(message)) {
    return "Focalx-rapporten er registrert, men PDF-filen finnes ikke lenger på serveren. Last opp rapporten på nytt og lagre registreringen.";
  }

  if (/No Focalx file found for this record/i.test(message)) {
    return "Ingen Focalx-PDF er lagret for denne registreringen.";
  }

  if (/Could not process request|Could not handle Focalx file request/i.test(message)) {
    return "Kunne ikke hente Focalx-PDF akkurat nå. Prøv igjen. Hvis feilen fortsetter, last opp rapporten på nytt og lagre registreringen.";
  }

  if (status === 404) {
    return "Focalx-PDF ble ikke funnet på serveren. Last opp rapporten på nytt og lagre registreringen.";
  }

  if (status >= 500) {
    return "Serverfeil ved nedlasting av Focalx-PDF. Prøv igjen om litt.";
  }

  return message || "Kunne ikke laste ned Focalx-PDF.";
}

async function request(path, { method = "GET", token, body } = {}) {
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const rawText = await response.text();
  let data = {};
  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch (_error) {
    data = rawText ? { error: rawText } : {};
  }

  if (!response.ok) {
    const message = data.error || data.message || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

function shouldRetryAlternateEndpoint(error, method = "GET") {
  const m = String(method || "GET").toUpperCase();
  const message = String(error && error.message ? error.message : "");
  if (new RegExp(`Cannot\\s+${m}\\s+\\/api\\/`, "i").test(message)) return true;
  if (/Request failed \((404|405)\)/i.test(message)) return true;
  if (/Not Found/i.test(message)) return true;
  return false;
}

async function requestWithFallback(paths, options) {
  const safePaths = Array.isArray(paths) ? paths : [paths];
  let lastError;

  for (let i = 0; i < safePaths.length; i += 1) {
    const path = safePaths[i];
    try {
      return await request(path, options);
    } catch (error) {
      lastError = error;
      const hasMore = i < safePaths.length - 1;
      if (!hasMore || !shouldRetryAlternateEndpoint(error, options?.method)) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Request failed");
}

export function loginUser(email, password) {
  return request("/auth/login", {
    method: "POST",
    body: { email, password },
  });
}

export function getCurrentUser(token) {
  return request("/auth/me", { token });
}

export function getVehicles(token) {
  return request("/vehicles", { token });
}

export function createVehicle(token, record) {
  return request("/vehicles", {
    method: "POST",
    token,
    body: { record },
  });
}

export function updateVehicle(token, id, record) {
  return request(`/vehicles/${id}`, {
    method: "PUT",
    token,
    body: { record },
  });
}

export function removeVehicle(token, id) {
  return request(`/vehicles/${id}`, {
    method: "DELETE",
    token,
  });
}

export function removeVehiclesBulk(token, ids) {
  return request("/vehicles", {
    method: "DELETE",
    token,
    body: { ids },
  });
}

export function uploadVehicleFocalx(token, id, fileData) {
  const encodedId = encodeURIComponent(id);
  return requestWithFallback([
    `/vehicles/${encodedId}/focalx`,
    `/vehicles/focalx?id=${encodedId}`,
  ], {
    method: "POST",
    token,
    body: fileData,
  });
}

export function deleteVehicleFocalx(token, id) {
  const encodedId = encodeURIComponent(id);
  return requestWithFallback([
    `/vehicles/${encodedId}/focalx`,
    `/vehicles/focalx?id=${encodedId}`,
  ], {
    method: "DELETE",
    token,
  });
}

export async function downloadVehicleFocalx(token, id, filename = "focalx.pdf") {
  const encodedId = encodeURIComponent(id);
  const paths = [
    `/vehicles/${encodedId}/focalx?download=1`,
    `/vehicles/focalx?id=${encodedId}&download=1`,
  ];

  let response = null;
  let finalError = null;
  for (let i = 0; i < paths.length; i += 1) {
    const path = paths[i];
    response = await fetch(`${API_BASE}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      finalError = null;
      break;
    }

    const data = await response.json().catch(() => ({}));
    const rawMessage = data.error || data.message || `Request failed (${response.status})`;
    const message = toFriendlyFocalxDownloadMessage(rawMessage, response.status);
    finalError = new Error(message);
    finalError.status = response.status;

    const hasMore = i < paths.length - 1;
    if (!hasMore || !shouldRetryAlternateEndpoint(finalError, "GET")) {
      throw finalError;
    }
  }

  if (!response || !response.ok) {
    throw finalError || new Error("Kunne ikke laste ned Focalx-PDF.");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function getAdminCompanies(token) {
  return request("/admin/companies", { token });
}

export function createAdminCompany(token, companyData) {
  const body = typeof companyData === "string" ? { name: companyData } : companyData;
  return request("/admin/companies", {
    method: "POST",
    token,
    body,
  });
}

export function updateAdminCompany(token, id, companyData) {
  return request(`/admin/companies?id=${encodeURIComponent(id)}`, {
    method: "PUT",
    token,
    body: typeof companyData === "string" ? { name: companyData } : companyData,
  });
}

export function getAdminUsers(token) {
  return request("/admin/users", { token });
}

export function createAdminUser(token, userData) {
  return request("/admin/users", {
    method: "POST",
    token,
    body: userData,
  });
}

export function updateAdminUser(token, id, userData) {
  return request(`/admin/users?id=${encodeURIComponent(id)}`, {
    method: "PUT",
    token,
    body: userData,
  });
}

export function deleteAdminUser(token, id) {
  return request(`/admin/users?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
    token,
  });
}
